import type { AddressbookTranslations } from './types'

/**
 * English translations for Addressbook domain
 */
export const enTranslations: AddressbookTranslations = {
  // Page/Section level
  title: "Address Book",
  subtitle: "Manage your locations and addresses",
  
  // Actions
  addAddress: "Add New Address", 
  editAddress: "Edit Address",
  deleteAddress: "Delete Address",
  exportComingSoon: "Export functionality will be available soon.",
  
  // Drawer Headers
  addAddressDescription: "Fill in the details to add a new address",
  editAddressDescription: "Update the address details below",
  
  // Section Headers
  basicInfo: "Basic Information",
  addressDetails: "Address Details",
  contactInfo: "Contact Information",
  coordinates: "Coordinates",
  
  // Search and states
  searchPlaceholder: "Search addresses...",
  noAddresses: "No Addresses Found",
  noAddressesDescription: "You haven't added any addresses yet. Add your first address to get started.",
  addFirst: "Add Your First Address",
  
  // Form fields
  locationName: "Location Name",
  locationNamePlaceholder: "e.g., Main Office, Warehouse A",
  locationType: "Location Type",
  selectType: "Select location type",
  selectLocationType: "Select location type (optional)",
  noLocationType: "No location type",
  streetAddress: "Street Address", 
  streetAddressPlaceholder: "Enter street address, building name, etc.",
  region: "Region",
  selectRegion: "Select region",
  city: "City",
  selectCity: "Select city",
  selectRegionFirst: "Select region first",
  country: "Country",
  selectCountry: "Select country",
  selectCountryFirst: "Select country first",
  noCitiesAvailable: "No cities available",
  postalCode: "Postal Code",
  postalCodePlaceholder: "Enter postal code",
  latitude: "Latitude",
  longitude: "Longitude", 
  contactName: "Contact Name",
  contactNamePlaceholder: "Enter contact person name",
  contactPhone: "Contact Phone",
  contactPhonePlaceholder: "+966 50 XXX XXXX",
  contactEmail: "Contact Email",
  address: "Address",
  notes: "Notes",
  tags: "Tags",
  timezone: "Timezone",
  
  // Address Types
  site: "Site",
  office: "Office", 
  warehouse: "Warehouse",
  home: "Home",
  plant: "Plant",
  customer: "Customer",
  
  // Status
  defaultAddress: "Default Address",
  favorite: "Favorite",
  
  // Toast Messages
  addressCreated: "Address Created",
  addressCreatedDescription: "The new address has been added successfully.",
  addressUpdated: "Address Updated", 
  addressUpdatedDescription: "The address details have been updated.",
  addressDeleted: "Address Deleted",
  addressDeletedDescription: "The address has been removed.",
  
  // Delete Dialog
  deleteTitle: "Delete Address",
  deleteDescription: "Are you sure you want to delete this address? This action cannot be undone.",
  deleteConfirm: "Yes, Delete",
  
  // Filter Options
  filterAll: "All Types",
  filterSite: "Sites",
  filterOffice: "Offices", 
  filterWarehouse: "Warehouses",
  filterHome: "Home",
  filterPlant: "Plants",
  filterCustomer: "Customers",
  
  // Stats
  totalAddresses: "Total Addresses",
  activeAddresses: "Active",
  inactiveAddresses: "Inactive",
  
  // Table Headers
  table: {
    name: "Name",
    type: "Type",
    address: "Address",
    city: "City",
    contact: "Contact", 
    status: "Status"
  },
  
  // Contact Types
  contactType: {
    gate_manager: "Gate Manager",
    dock_supervisor: "Dock Supervisor",
    security: "Security",
    primary_contact: "Primary Contact",
    emergency_contact: "Emergency Contact"
  },
  
  // Location Type descriptions (extended)
  locationTypeDescriptions: {
    site: "Construction Site",
    office: "Office Building", 
    warehouse: "Warehouse",
    home: "Home Address",
    plant: "Manufacturing Plant",
    customer: "Customer Location"
  },
  
  // Validation
  validation: {
    nameRequired: "Location name is required",
    addressRequired: "Street address is required",
    countryRequired: "Country is required",
    cityRequired: "City is required"
  },
  
  // Additional Form
  form: {
    englishSection: "English",
    streetSection: "Street address",
    locationNameEn: "Location name (English)",
    locationNameAr: "Location name (Arabic)",
    locationNameArPlaceholder: "Arabic name (optional)",
    streetAddressEn: "Street address (English)",
    streetAddressAr: "Street address (Arabic)",
    streetAddressArPlaceholder: "Arabic street address (optional)",
    addressLine2: "Address Line 2",
    addressLine2Placeholder: "Apartment, suite, unit, etc. (optional)",
    addressLine2En: "Address line 2 (English)",
    addressLine2Ar: "Address line 2 (Arabic)",
    addressLine2ArPlaceholder: "Arabic line 2 (optional)",
  },

  // Additional keys
  addressId: "Address ID",
  createNewLocation: "Create New Location",
  createNewLocationHint: "Add a new delivery address",
  searchLocations: "Search locations...",
  noMatchingAddresses: "No matching addresses",
  noAddressesFound: "No addresses found",

  // Widget/Stats keys
  locations: "Locations",
  sites: "Sites",
  offices: "Offices",
  total: "Total",
  active: "Active",
  inactive: "Inactive",

  // Table column keys
  name: "Name",
  actions: "Actions",

  // Delete confirmation with placeholder
  deleteConfirmation: "Are you sure you want to delete {{name}}? This action cannot be undone.",

  // Standalone convenience keys (duplicates of nested keys for easier access)
  type: "Type",
  contact: "Contact",
  status: "Status",

  // Contact type standalone keys (duplicates of contactType for easier access)
  gate_manager: "Gate Manager",
  dock_supervisor: "Dock Supervisor",
  security: "Security",
  primary_contact: "Primary Contact",
  emergency_contact: "Emergency Contact",

  // Validation standalone keys (duplicates of validation for easier access)
  nameRequired: "Location name is required",
  addressRequired: "Street address is required",
  countryRequired: "Country is required",
  cityRequired: "City is required",

  // Form standalone keys (duplicates of form for easier access)
  addressLine2: "Address Line 2",
  addressLine2Placeholder: "Apartment, suite, unit, etc. (optional)",
}
